import React, { useEffect, useState } from "react";

// ASSETS
import ArrowLeft from "assets/svg/ArrowDown.svg";
import { ReactComponent as NoDataDark } from "assets/svg/NoDataDark.svg";

// STYLES
import {
  BackToGuidesButton,
  BackToGuidesImage,
  BackToWrapper,
  ContactCardsWrapper,
  ContentSectionWrapper,
  HeaderSectionWrapper,
  InstitutionsContactContainer,
  NoDataContainer,
  SvgContainer,
  TitleSectionWrapper,
  TitleWrapper,
} from "./InstitutionsContactStyled";

// LIBRARIES
import usePagination from "hooks/usePagination";
import { useTranslation } from "react-i18next";

// MISC
import { IProps, InstitutionContact, mapInstitutionContact } from "./InstitutionsContactModel";
import { TextType } from "components/Atoms/CustomText/CustomTextModel";
import { COLORS } from "constants/variables";
import aboutService from "services/aboutService";

// REDUX
import { useSelector } from "react-redux";
import { accessibilityBiggerCursorSelector } from "slices/UISlice/uiSliceSelectors";

// COMPONENTS
import HeaderTab from "components/HeaderTab/HeaderTab";
import Pagination from "components/Atoms/Paginations/Pagination";
import CustomText from "components/Atoms/CustomText/CustomText";
import InstitutionContactCard from "./InstitutionContactCard";

const InstitutionsContact = (props: IProps) => {
  // PROPERTIES
  const ELEMENTS_PER_PAGE = 10;

  // STATE CONSTANTS
  const [institutions, setInstitutions] = useState<any>(null);

  // LIBRARY CONSTANTS
  const biggerCursor = useSelector(accessibilityBiggerCursorSelector);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const { t } = useTranslation();

  // API REQUESTS

  // LIFE CYCLE
  useEffect(() => {
    aboutService
      .getInstitutions()
      .then((response) => {
        // console.log("getInstitutions response", response);
        setInstitutions(response);
      })
      .catch((err) => {
        console.log("getInstitutions error", err);
      });
  }, []);

  const getContactData = (headType) => {
    return (
      institutions?.data?.filter((item) => item.attributes.head === headType) ||
      []
    )
      .map(mapInstitutionContact)
      .sort((a, b) => {
        if (a.county.toLowerCase() < b.county.toLowerCase()) return -1;
        if (a.county.toLowerCase() > b.county.toLowerCase()) return 1;
        if (a.city.toLowerCase() < b.city.toLowerCase()) return -1;
        if (a.city.toLowerCase() > b.city.toLowerCase()) return 1;
        return 0;
      });
  };

  const IGSUContactData = getContactData("IGSU");
  const IGAvContactData = getContactData("IGAv");
  const ISUContactData = getContactData("ISU");
  const UPUContactData = getContactData("UPU");
  const SalvamontContactData = getContactData("Salvamont");
  const SAJContactData = getContactData("SAJ");
  const SalvaspeoContactData = getContactData("Salvaspeo");

  const usePaginatedData = (data) => {
    return usePagination(data, ELEMENTS_PER_PAGE);
  };

  const {
    currentPage: currentIGSUContactPage,
    totalPages: IGSUContactPages,
    currentData: currentIGSUContact,
    handlePageChange: handleIGSUContactPageChange,
  } = usePaginatedData(IGSUContactData);

  const {
    currentPage: currentIGaVContactPage,
    totalPages: IGaVContactPages,
    currentData: currentIGaVContact,
    handlePageChange: handleIGaVContactPageChange,
  } = usePaginatedData(IGAvContactData);

  const {
    currentPage: currentISUContactPage,
    totalPages: ISUContactPages,
    currentData: currentISUContact,
    handlePageChange: handleISUContactPageChange,
  } = usePaginatedData(ISUContactData);

  const {
    currentPage: currentUPUContactPage,
    totalPages: UPUContactPages,
    currentData: currentUPUContact,
    handlePageChange: handleUPUContactPageChange,
  } = usePaginatedData(UPUContactData);

  const {
    currentPage: currentSalvamontContactPage,
    totalPages: SalvamontContactPages,
    currentData: currentSalvamontContact,
    handlePageChange: handleSalvamontContactPageChange,
  } = usePaginatedData(SalvamontContactData);

  const {
    currentPage: currentSAJContactPage,
    totalPages: SAJContactPages,
    currentData: currentSAJContact,
    handlePageChange: handleSAJContactPageChange,
  } = usePaginatedData(SAJContactData);

  const {
    currentPage: currentSalvaspeoContactPage,
    totalPages: SalvaspeoContactPages,
    currentData: currentSalvaspeoContact,
    handlePageChange: handleSalvaspeoContactPageChange,
  } = usePaginatedData(SalvaspeoContactData);

  const content = (tabContent: InstitutionContact[], allContacts: InstitutionContact[]) => {
    const showWebsite = allContacts.some(contact => !!contact.website);
    return (
      <ContactCardsWrapper $hasWebsites={showWebsite}>
        {tabContent?.map(contact => (
          <InstitutionContactCard key={contact.id} contact={contact} showWebsite={showWebsite} />
        ))}
      </ContactCardsWrapper>
    );
  };

  const tabs = [
    {
      id: 0,
      tabTitle: "IGSU",
      content: content(currentIGSUContact, IGSUContactData),
      totalPages: IGSUContactPages,
      currentPage: currentIGSUContactPage,
      handlePageChange: handleIGSUContactPageChange,
    },
    {
      id: 1,
      tabTitle: "IGAv",
      content: content(currentIGaVContact, IGAvContactData),
      totalPages: IGaVContactPages,
      currentPage: currentIGaVContactPage,
      handlePageChange: handleIGaVContactPageChange,
    },
    {
      id: 2,
      tabTitle: "ISU",
      content: content(currentISUContact, ISUContactData),
      totalPages: ISUContactPages,
      currentPage: currentISUContactPage,
      handlePageChange: handleISUContactPageChange,
    },
    {
      id: 3,
      tabTitle: "UPU",
      content: content(currentUPUContact, UPUContactData),
      totalPages: UPUContactPages,
      currentPage: currentUPUContactPage,
      handlePageChange: handleUPUContactPageChange,
    },
    {
      id: 4,
      tabTitle: "Salvamont",
      content: content(currentSalvamontContact, SalvamontContactData),
      totalPages: SalvamontContactPages,
      currentPage: currentSalvamontContactPage,
      handlePageChange: handleSalvamontContactPageChange,
    },
    {
      id: 5,
      tabTitle: "SAJ",
      content: content(currentSAJContact, SAJContactData),
      totalPages: SAJContactPages,
      currentPage: currentSAJContactPage,
      handlePageChange: handleSAJContactPageChange,
    },
    {
      id: 6,
      tabTitle: "Salvaspeo",
      content: content(currentSalvaspeoContact, SalvaspeoContactData),
      totalPages: SalvaspeoContactPages,
      currentPage: currentSalvaspeoContactPage,
      handlePageChange: handleSalvaspeoContactPageChange,
    },
  ];

  // EVENT HANDLERS
  const handleTabSelect = (selectedTabId: number) => {
    setSelectedTab(selectedTabId);
  };

  return (
    <InstitutionsContactContainer>
      <TitleSectionWrapper>
        <BackToWrapper
          onClick={() => window.history.back()}
          biggerCursor={biggerCursor}
        >
          <BackToGuidesImage src={ArrowLeft} alt="backIcon" />
          <BackToGuidesButton>
            <CustomText
              text={t("view.about.contactInstitutions.aboutDSU")}
              type={TextType.H5bold}
              color={COLORS.MetalicSilver}
            />
          </BackToGuidesButton>
        </BackToWrapper>
        <TitleWrapper>
          <CustomText
            type={TextType.H2}
            text={t("view.about.contactInstitutions.title")}
          />
        </TitleWrapper>
      </TitleSectionWrapper>

      <HeaderSectionWrapper>
        <HeaderTab tabs={tabs} onTabSelect={handleTabSelect} />
      </HeaderSectionWrapper>
      <ContentSectionWrapper>
        {tabs[selectedTab].content.props.children?.length > 0 ? (
          tabs[selectedTab].content
        ) : (
          <NoDataContainer>
            <SvgContainer>
              <NoDataDark />
            </SvgContainer>
            <CustomText
              text={t("app.noData")}
              type={TextType.H4}
              color={COLORS.MetalicSilver}
            />
          </NoDataContainer>
        )}

        {tabs[selectedTab].totalPages > 1 && (
          <Pagination
            totalPages={tabs[selectedTab].totalPages}
            currentPage={tabs[selectedTab].currentPage}
            onPageChange={tabs[selectedTab].handlePageChange}
          />
        )}
      </ContentSectionWrapper>
    </InstitutionsContactContainer>
  );
};

export default InstitutionsContact;
